import { filePreviewUrl } from '../../../utils/filePreview';

// Shared placeholder used whenever a real page thumbnail can't be rendered.
export const PLACEHOLDER_THUMBNAIL = '/img/AI-feedback-Image.svg';

/**
 * Deliberately not a reference check: the placeholder reaches pages both as this
 * constant and, once an origin has been prefixed, as an absolute url. Treating
 * the prefixed form as a real thumbnail is how a page gets skipped by everything
 * that tries to draw it.
 */
export const isPlaceholderThumbnail = (url = '') =>
  !url || url === PLACEHOLDER_THUMBNAIL || url.includes('AI-feedback-Image.svg');

/**
 * An already-stored image is its own thumbnail. Relative urls are made absolute
 * so the result is comparable with everything else that holds one. Stored links
 * load through the API's streamed preview: the thumbnail is only ever drawn,
 * never saved (draft page snapshots store the placeholder).
 */
export const imageThumbnailUrl = (fileUrl) =>
  filePreviewUrl(fileUrl?.startsWith('http') ? fileUrl : `${window.location.origin}${fileUrl}`);

// Keep thumbnails small enough to live in React state alongside every other
// page for the whole wizard, without holding a revocable object URL.
const THUMBNAIL_MAX_EDGE = 1200;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image failed to load'));
    img.src = src;
  });

const scaleFor = (img) =>
  Math.min(1, THUMBNAIL_MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));

const drawToCanvas = (img, scale) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const context = canvas.getContext('2d');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
};

/**
 * Render an image File to a self-contained data URL thumbnail.
 *
 * Deliberately NOT an object URL: page thumbnails outlive the step that created
 * them (they are still rendered on Organise pages and Review), so anything
 * revocable ends up as a broken preview the moment this step unmounts.
 *
 * @param {File} file
 * @returns {Promise<string>} data URL, or the placeholder if decoding fails
 */
export const generateImageThumbnail = async (file) => {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    return drawToCanvas(img, scaleFor(img)).toDataURL('image/jpeg', 0.85);
  } catch (error) {
    // Image thumbnail generation failed
    return PLACEHOLDER_THUMBNAIL;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
